import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers } from '../../../reduxLogic/reducers/auth/getAllUsersSlice';
import {
    getExpenses,
    getExpensesWithAllocation,
    addExpense,
    uploadExpenseProofs,
    getUserDetails,
    deleteExpense,
    getAdvances,
    allocateExpenseToAdvance,
    getAllExpensesGlobal,
    updateExpenseApprovalStatus,
    getAllApiariesService,
    getSupervisors
} from '../../../../../../services/apiaryService';
import { readCookie } from '../../../../cookie';
import { checkIsAdmin } from '../../../Advances/authHelper';
import close from '../../../assets/images/close.png';
import * as Styles from '../../../Farmers/components/FarmerForm/style';
import { Form, Row, Col, InputGroup, Button, Modal, Badge } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { toast } from 'react-toastify';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import GenericExportCSVModal from '../../../shared/GenericExportCSVModal';

const isApiaryIdValid = (id) => {
    if (!id) return false;
    if (typeof id === 'string') {
        const trimmed = id.trim().toLowerCase();
        return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined' && trimmed !== 'n/a' && trimmed !== 'none';
    }
    if (typeof id === 'object') {
        const subId = id._id || id.$oid || id.id;
        return isApiaryIdValid(subId);
    }
    return false;
};

const isApiaryNameValid = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim().toLowerCase();
    return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined' && trimmed !== 'n/a' && trimmed !== 'none' && trimmed !== 'unknown';
};

const checkIsApiary = (row) => {
    if (!row) return false;
    if (row.expense_type === 'non_apiary' || row.type === 'non_apiary' || row.is_apiary === false || row.tx_type === 'NON_APIARY_EXPENSE') return false;
    if (row.expense_type === 'apiary' || row.type === 'apiary' || row.is_apiary === true || row.tx_type === 'APIARY_EXPENSE') return true;
    
    // Check if notes/remarks indicate non-apiary overhead
    if ((row.notes && row.notes.toUpperCase().includes('NON-APIARY')) || (row.remarks && row.remarks.toUpperCase().includes('NON-APIARY'))) {
        return false;
    }
    
    // Check valid apiary id, apiary name, or movement
    if (isApiaryIdValid(row.apiary_id)) return true;
    if (isApiaryNameValid(row.apiary_name)) return true;
    if (isApiaryIdValid(row.movement_id)) return true;
    if (row.category && typeof row.category === 'string' && row.category.toUpperCase().includes('APIARY')) return true;

    return false;
};

const resolveSupervisorName = (row, supervisorList = [], userNamesMap = {}) => {
    if (!row) return null;
    if (row.reporting_to_name && typeof row.reporting_to_name === 'string') return row.reporting_to_name;
    if (row.reporting_to && typeof row.reporting_to === 'object') {
        return row.reporting_to.name || row.reporting_to.username || row.reporting_to.full_name || row.reporting_to.email || null;
    }
    const rawRepId = row.reporting_to || row.supervisor_id || row.supervisorId;
    if (rawRepId) {
        const strId = String(rawRepId);
        const sup = supervisorList.find(s => String(s.id || s._id || s.user_id) === strId);
        if (sup) return sup.name || sup.username || sup.full_name || 'Supervisor';
        if (userNamesMap[strId]) return userNamesMap[strId];
        if (typeof rawRepId === 'string' && !rawRepId.match(/^[0-9a-fA-F]{24}$/)) {
            return rawRepId;
        }
    }
    if (row.supervisor_name) return row.supervisor_name;
    return null;
};

const resolveEmployeeId = (row, supervisors = [], allUsers = []) => {
    if (!row) return null;

    if (row.user_employee_id) return row.user_employee_id;
    if (row.employee_id) return row.employee_id;
    if (row.paid_by_employee_id) return row.paid_by_employee_id;
    if (row.created_by_employee_id) return row.created_by_employee_id;
    if (row.employeeId) return row.employeeId;
    if (row.employeeCode) return row.employeeCode;
    if (row.paid_by && typeof row.paid_by === 'object' && (row.paid_by.employee_id || row.paid_by.user_employee_id)) {
        return row.paid_by.employee_id || row.paid_by.user_employee_id;
    }
    if (row.user_id && typeof row.user_id === 'object' && (row.user_id.employee_id || row.user_id.user_employee_id)) {
        return row.user_id.employee_id || row.user_id.user_employee_id;
    }

    const rawId = row.paid_by || row.created_by || row.user_id || row.expensed_by || row.issued_to || row.memberId;
    const strId = rawId ? (typeof rawId === 'object' ? String(rawId._id || rawId.id || rawId.$oid || '') : String(rawId)) : null;

    if (strId) {
        const s = (supervisors || []).find(sup => String(sup._id || sup.id || sup.user_id) === strId);
        if (s?.employee_id || s?.employeeId || s?.user_employee_id) return s.employee_id || s.employeeId || s.user_employee_id;

        const u = (allUsers || []).find(usr => {
            const uId = String(usr._id || usr.id || usr.userInfo?._id || '');
            return uId === strId;
        });
        const uEmp = u?.employee_id || u?.userInfo?.employee_id || u?.employeeId || u?.user_employee_id;
        if (uEmp) return uEmp;
    }

    const rawName = row.user_name || row.paid_by_name || row.created_by_name || row.memberName || row.issued_to_name || (typeof row.issued_to === 'object' ? row.issued_to?.name : null) || row.incurred_by_role;
    if (rawName && typeof rawName === 'string') {
        const targetName = rawName.trim().toLowerCase();

        const s = (supervisors || []).find(sup => {
            const sName = String(sup.name || sup.user_name || sup.username || sup.fullName || '').trim().toLowerCase();
            return sName === targetName;
        });
        if (s?.employee_id || s?.employeeId || s?.user_employee_id) return s.employee_id || s.employeeId || s.user_employee_id;

        const u = (allUsers || []).find(usr => {
            const uName = String(usr.name || usr.userInfo?.name || usr.username || '').trim().toLowerCase();
            return uName === targetName;
        });
        const uEmp = u?.employee_id || u?.userInfo?.employee_id || u?.employeeId || u?.user_employee_id;
        if (uEmp) return uEmp;
    }

    return null;
};

const EXPENSE_CATEGORIES = [
    'TRANSPORT',
    'SUGAR',
    'LABOUR',
    'MEDICINE',
    'EQUIPMENT',
    'BOX_MAINTENANCE',
    'OFFICE',
    'MARKETING',
    'TRAVEL',
    'OTHER'
];

const AddExpenseModal = ({ show, onClose, onSuccess, currentApiaryId }) => {
    const [formData, setFormData] = useState({
        expense_type: currentApiaryId ? 'apiary' : 'apiary',
        apiary_id: currentApiaryId || '',
        category: 'TRANSPORT',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        supervisor_id: '',
        incurred_by_role: '',
        user_name: '',
        notes: '',
    });
    const [proofFiles, setProofFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiaries, setApiaries] = useState([]);
    const [supervisors, setSupervisors] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [apis, sups] = await Promise.all([
                    getAllApiariesService().catch(() => []),
                    getSupervisors().catch(() => [])
                ]);
                if (Array.isArray(apis)) setApiaries(apis);
                if (Array.isArray(sups)) {
                    setSupervisors(sups);
                    if (sups.length > 0) {
                        const first = sups[0];
                        setFormData(prev => ({
                            ...prev,
                            supervisor_id: first._id || first.id || '',
                            user_name: first.name || first.user_name || first.fullName || 'Supervisor',
                            incurred_by_role: first.name || first.user_name || first.fullName || 'Supervisor',
                        }));
                    }
                }
            } catch (err) {
                console.warn('Could not load apiaries or supervisors:', err);
            }
        };
        load();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'supervisor_id') {
            const selectedSup = supervisors.find(s => (s._id || s.id) === value);
            const supName = selectedSup?.name || selectedSup?.user_name || selectedSup?.fullName || 'Supervisor';
            setFormData(prev => ({
                ...prev,
                supervisor_id: value,
                user_name: supName,
                incurred_by_role: supName,
            }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setProofFiles(Array.from(e.target.files));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.amount || Number(formData.amount) <= 0) {
            toast.error('Please enter a valid expense amount');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                user_id: formData.supervisor_id || '5e2fb340caa96c5efc692da7',
                amount: Number(formData.amount),
                category: formData.category,
                expense_date: formData.expense_date,
                notes: formData.notes,
                remarks: formData.notes,
                user_name: formData.user_name || 'Admin',
                incurred_by_role: formData.incurred_by_role || formData.user_name || 'Supervisor',
                approval_status: 'PENDING',
                expense_type: formData.expense_type,
            };

            if (formData.expense_type === 'apiary') {
                const chosenApiaryId = formData.apiary_id || currentApiaryId;
                const chosenObj = apiaries.find(a => (a.apiary_id || a._id) === chosenApiaryId);
                payload.apiary_id = chosenApiaryId;
                payload.apiary_name = chosenObj?.apiary_name || chosenObj?.name || 'Apiary';
            } else {
                const fallbackApiary = apiaries[0];
                payload.apiary_id = fallbackApiary?.apiary_id || fallbackApiary?._id;
                payload.apiary_name = 'Non-Apiary Overhead';
                payload.notes = `[NON-APIARY] ${formData.notes || ''}`.trim();
                payload.remarks = `[NON-APIARY] ${formData.notes || ''}`.trim();
                payload.expense_type = 'non_apiary';
            }

            const created = await addExpense(payload);
            const expenseId = created?._id || created?.expense?._id || created?.expense_id;

            if (proofFiles.length > 0 && expenseId) {
                const fileData = new FormData();
                proofFiles.forEach(file => {
                    fileData.append('photos', file);
                });
                try {
                    await uploadExpenseProofs(expenseId, fileData);
                } catch (uploadErr) {
                    console.warn('Proof upload failed:', uploadErr);
                }
            }

            toast.success(`${formData.expense_type === 'apiary' ? 'Apiary' : 'Non-Apiary'} expense recorded successfully!`);
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Failed to add expense:', err);
            toast.error(err.response?.data?.message || err.message || 'Failed to add expense');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton style={{ borderBottom: '1px solid #f1f5f9', padding: '18px 24px' }}>
                <Modal.Title style={{ fontWeight: 800, color: '#1e293b' }}>
                    <i className="fa fa-plus-circle text-primary mr-2"></i> Add New Expense
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body style={{ padding: '24px' }}>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Expense Nature / Type</Form.Label>
                            <div style={{ display: 'flex', gap: '20px', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <Form.Check
                                    type="radio"
                                    id="type-apiary"
                                    name="expense_type"
                                    value="apiary"
                                    label="🐝 Apiary Expense"
                                    checked={formData.expense_type === 'apiary'}
                                    onChange={handleChange}
                                    style={{ fontWeight: 600, color: '#0369a1' }}
                                />
                                <Form.Check
                                    type="radio"
                                    id="type-non-apiary"
                                    name="expense_type"
                                    value="non_apiary"
                                    label="🏢 Non-Apiary Expense (Admin / Travel / Ops)"
                                    checked={formData.expense_type === 'non_apiary'}
                                    onChange={handleChange}
                                    style={{ fontWeight: 600, color: '#475569' }}
                                />
                            </div>
                        </Col>
                    </Row>

                    {formData.expense_type === 'apiary' && !currentApiaryId && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Select Apiary *</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="apiary_id"
                                        value={formData.apiary_id}
                                        onChange={handleChange}
                                        required
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <option value="">-- Choose Apiary --</option>
                                        {apiaries.map(a => (
                                            <option key={a.apiary_id || a._id} value={a.apiary_id || a._id}>
                                                {a.apiary_name || a.name} {a.village ? `(${a.village}, ${a.district || ''})` : ''}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Amount (₹) *</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="amount"
                                    placeholder="e.g. 2500"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    required
                                    style={{ borderRadius: '8px', fontWeight: 700, fontSize: '16px', color: '#00b894' }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Category *</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    required
                                    style={{ borderRadius: '8px', fontWeight: 600 }}
                                >
                                    {EXPENSE_CATEGORIES.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Expense Date *</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="expense_date"
                                    value={formData.expense_date}
                                    onChange={handleChange}
                                    required
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Expensed By / Supervisor *</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="supervisor_id"
                                    value={formData.supervisor_id}
                                    onChange={handleChange}
                                    style={{ borderRadius: '8px', fontWeight: 600 }}
                                >
                                    {supervisors.map(s => (
                                        <option key={s._id || s.id} value={s._id || s.id}>
                                            {s.name || s.user_name || s.fullName || 'Supervisor'} {s.phone_number ? `(${s.phone_number})` : ''}
                                        </option>
                                    ))}
                                    <option value="5e2fb340caa96c5efc692da7">Admin User</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Remarks / Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="notes"
                                    placeholder="Add any description or justification..."
                                    value={formData.notes}
                                    onChange={handleChange}
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>Receipts / Bill Proofs (Images/PDFs)</Form.Label>
                                <Form.Control
                                    type="file"
                                    multiple
                                    accept="image/*,application/pdf"
                                    onChange={handleFileChange}
                                    style={{ padding: '6px' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f1f5f9' }}>
                    <Button variant="light" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={isSubmitting} style={{ fontWeight: 700, padding: '8px 24px' }}>
                        {isSubmitting ? 'Saving...' : 'Create Expense'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// Styled Components
const MainContainer = styled.div`
    padding: ${props => props.isGlobal ? '30px' : '20px 0'};
    background: ${props => props.isGlobal ? '#f8f9fa' : 'transparent'};
    min-height: ${props => props.isGlobal ? '100vh' : 'auto'};
    margin-left: ${props => props.isGlobal ? '15%' : '0'};
    width: ${props => props.isGlobal ? '85%' : '100%'};

    @media (max-width: 500px) {
        margin-left: 0;
        width: 100%;
        padding: 15px;
    }

    .modern-table {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        overflow: hidden;
        margin-bottom: 20px;
        border: 1px solid #f0f0f0;
    }

    .modern-table thead th {
        border-bottom: 2px solid #f1f2f6 !important;
        background: #fafafa;
        color: #636e72;
        font-weight: 700;
        font-size: 13px;
        padding: 18px 15px;
        text-transform: uppercase;
        letter-spacing: 1px;
        border-top: none !important;
    }

    .add-expense-btn {
        background: linear-gradient(135deg, #EF5A20 0%, #ff7e5f 100%);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 12px;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 10px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 15px rgba(239, 90, 32, 0.2);
        cursor: pointer;
    }

    .add-expense-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(239, 90, 32, 0.3);
        filter: brightness(1.1);
    }

    .modern-table tbody tr {
        transition: all 0.3s ease;
    }

    .modern-table tbody tr:hover {
        background-color: #f8f9ff !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.02);
    }

    .modern-table tbody td {
        border-top: 1px solid #f8f9fa;
        padding: 16px 15px;
        color: #2d3436;
        vertical-align: middle;
        font-size: 14px;
    }
`;

const TotalBadge = styled.div`
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #2d3436 0%, #000000 100%);
    color: white;
    padding: 12px 24px;
    border-radius: 14px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    flex-wrap: wrap;
    gap: 15px;
`;

const FilterCard = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 25px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
    border: 1px solid #f0f0f0;
`;

const ActionButton = styled.button`
    background: none;
    border: none;
    padding: 6px 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &.view {
        color: #0984e3;
        background: #e1705515;
        &:hover { background: #0984e3; color: white; }
    }

    &.delete {
        color: #d63031;
        background: #d6303115;
        &:hover { background: #d63031; color: white; }
    }
`;

const renderTravelDetails = (travel) => {
    if (!travel) return null;

    const mode = travel.mode_of_transport || travel.modeOfTransport || travel.mode || null;
    const distance = travel.distance_km != null ? travel.distance_km : (travel.distanceKm != null ? travel.distanceKm : travel.distance);
    const merchant = travel.merchant || travel.vendor || null;

    const parseLoc = (loc) => {
        if (!loc) return null;
        if (typeof loc === 'string') return { name: loc, address: '' };
        const name = loc.name || loc.place_name || loc.formattedAddress || loc.formatted_address || '';
        const address = loc.formattedAddress || loc.formatted_address || loc.address || '';
        return {
            name,
            address: address !== name ? address : ''
        };
    };

    const startLoc = parseLoc(travel.start_location || travel.startLocation);
    const endLoc = parseLoc(travel.end_location || travel.endLocation);
    const stops = Array.isArray(travel.stops) ? travel.stops.map(parseLoc).filter(Boolean) : [];

    const getTransportIcon = (m) => {
        if (!m) return 'fa-car';
        const lower = String(m).toLowerCase();
        if (lower.includes('bike') || lower.includes('motorcycle') || lower.includes('two wheeler')) return 'fa-motorcycle';
        if (lower.includes('auto') || lower.includes('rickshaw')) return 'fa-taxi';
        if (lower.includes('bus')) return 'fa-bus';
        if (lower.includes('cab') || lower.includes('taxi')) return 'fa-taxi';
        if (lower.includes('train') || lower.includes('rail') || lower.includes('public')) return 'fa-train';
        if (lower.includes('flight') || lower.includes('plane')) return 'fa-plane';
        return 'fa-car';
    };

    return (
        <div style={{ 
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
            border: '1px solid #bae6fd', 
            borderRadius: '12px', 
            padding: '16px', 
            marginBottom: '20px' 
        }}>
            <div style={{ 
                fontSize: '12px', 
                fontWeight: 800, 
                color: '#0369a1', 
                textTransform: 'uppercase', 
                letterSpacing: '.04em', 
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
            }}>
                <i className="fa fa-map-signs" style={{ fontSize: '14px', color: '#0284c7' }}></i> Travel & Commute Details
            </div>

            {/* Highlights Bar */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: (startLoc || endLoc) ? '14px' : '0' }}>
                {mode && (
                    <div style={{ 
                        background: '#ffffff', 
                        border: '1px solid #93c5fd', 
                        borderRadius: '8px', 
                        padding: '6px 12px', 
                        fontSize: '12.5px', 
                        fontWeight: 700, 
                        color: '#1e40af',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                    }}>
                        <i className={`fa ${getTransportIcon(mode)}`} style={{ color: '#2563eb' }}></i>
                        <span>{mode}</span>
                    </div>
                )}

                {distance != null && (
                    <div style={{ 
                        background: '#ffffff', 
                        border: '1px solid #86efac', 
                        borderRadius: '8px', 
                        padding: '6px 12px', 
                        fontSize: '12.5px', 
                        fontWeight: 700, 
                        color: '#166534',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                    }}>
                        <i className="fa fa-road" style={{ color: '#16a34a' }}></i>
                        <span>{Number(distance).toLocaleString('en-IN')} KM</span>
                    </div>
                )}

                {merchant && (
                    <div style={{ 
                        background: '#ffffff', 
                        border: '1px solid #fde68a', 
                        borderRadius: '8px', 
                        padding: '6px 12px', 
                        fontSize: '12.5px', 
                        fontWeight: 700, 
                        color: '#854d0e',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                    }}>
                        <i className="fa fa-shopping-bag" style={{ color: '#d97706' }}></i>
                        <span>Merchant/Vendor: <strong>{merchant}</strong></span>
                    </div>
                )}
            </div>

            {/* Route Timeline */}
            {(startLoc || endLoc) && (
                <div style={{ background: '#ffffff', borderRadius: '10px', padding: '14px', border: '1px solid #e0f2fe', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: '12px' }}>
                        Route & Commute Path
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Start Location */}
                        {startLoc && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <div style={{ 
                                    width: '22px', 
                                    height: '22px', 
                                    borderRadius: '50%', 
                                    background: '#dcfce7', 
                                    color: '#15803d', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    fontWeight: 800,
                                    flexShrink: 0,
                                    marginTop: '1px',
                                    border: '1px solid #86efac'
                                }}>
                                    🟢
                                </div>
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginRight: '4px' }}>Start Location:</span>
                                        {startLoc.name || startLoc.address}
                                    </div>
                                    {startLoc.address && startLoc.address !== startLoc.name && (
                                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                                            <i className="fa fa-map-marker text-muted mr-1"></i>{startLoc.address}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Intermediate Stops */}
                        {stops.map((stop, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <div style={{ 
                                    width: '22px', 
                                    height: '22px', 
                                    borderRadius: '50%', 
                                    background: '#fef3c7', 
                                    color: '#b45309', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    fontWeight: 800,
                                    flexShrink: 0,
                                    marginTop: '1px',
                                    border: '1px solid #fde68a'
                                }}>
                                    🟡
                                </div>
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginRight: '4px' }}>Stop #{idx + 1}:</span>
                                        {stop.name || stop.address}
                                    </div>
                                    {stop.address && stop.address !== stop.name && (
                                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                                            <i className="fa fa-map-marker text-muted mr-1"></i>{stop.address}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Destination Location */}
                        {endLoc && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <div style={{ 
                                    width: '22px', 
                                    height: '22px', 
                                    borderRadius: '50%', 
                                    background: '#fee2e2', 
                                    color: '#b91c1c', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    fontWeight: 800,
                                    flexShrink: 0,
                                    marginTop: '1px',
                                    border: '1px solid #fca5a5'
                                }}>
                                    🔴
                                </div>
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginRight: '4px' }}>Destination:</span>
                                        {endLoc.name || endLoc.address}
                                    </div>
                                    {endLoc.address && endLoc.address !== endLoc.name && (
                                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                                            <i className="fa fa-map-marker text-muted mr-1"></i>{endLoc.address}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const ExpenseDetailModal = ({ item, onClose, onUpdateStatus, reportingToName, isAdmin = true }) => {
    if (!item) return null;
    const travelDetails = item.travel_details || item.travelDetails || item.travel;
    return (
        <Modal show={true} onHide={onClose} centered size="lg">
            <Modal.Header closeButton style={{ borderBottom: 'none', padding: '20px 25px' }}>
                <Modal.Title style={{ fontWeight: 800, color: '#2d3436' }}>
                    <i className="fa fa-receipt text-primary mr-2"></i> Expense Details & Status Management
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '0 25px 25px 25px' }}>
                <Row className="mb-4">
                    <Col md={6}>
                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '12px' }}>
                            <div style={{ fontSize: '12px', color: '#b2bec3', fontWeight: 700, textTransform: 'uppercase' }}>Amount</div>
                            <div style={{ fontSize: '24px', fontWeight: 900, color: '#00b894' }}>₹ {item.amount?.toLocaleString()}</div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '12px' }}>
                            <div style={{ fontSize: '12px', color: '#b2bec3', fontWeight: 700, textTransform: 'uppercase' }}>Category</div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#0984e3' }}>{item.category}</div>
                        </div>
                    </Col>
                </Row>
                <Row className="mb-4">
                    <Col md={6}>
                        <div style={{ fontSize: '13px', color: '#636e72' }}><strong>Date:</strong> {new Date(item.expense_date || item.date).toLocaleDateString()}</div>
                        <div style={{ fontSize: '13px', color: '#636e72', marginTop: '6px' }}>
                            <strong>Reporting To:</strong>{' '}
                            {reportingToName ? (
                                <span style={{ background: '#f8fafc', color: '#334155', padding: '2px 8px', borderRadius: '4px', fontWeight: 600, border: '1px solid #e2e8f0' }}>
                                    <i className="fa fa-user-circle-o mr-1" style={{ color: '#64748b' }}></i>
                                    {reportingToName}
                                </span>
                            ) : (
                                <span style={{ color: '#94a3b8' }}>—</span>
                            )}
                        </div>
                        <div style={{ fontSize: '13px', color: '#636e72', marginTop: '6px' }}>
                            <strong>Approval Status:</strong>{' '}
                            <Badge variant={item.approval_status === 'APPROVED' ? 'success' : item.approval_status === 'REJECTED' ? 'danger' : 'warning'}>
                                {item.approval_status || 'PENDING'}
                            </Badge>
                        </div>
                        {(item.approved_by_name || item.approved_by) && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                <strong>Approved by:</strong> {item.approved_by_name || (typeof item.approved_by === 'object' ? (item.approved_by?.name || item.approved_by?.username) : null) || item.approved_by}
                            </div>
                        )}
                        {item.approval_comment && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', fontStyle: 'italic' }}>Comment: "{item.approval_comment}"</div>}
                    </Col>
                    <Col md={6}>
                        <div style={{ fontSize: '13px', color: '#636e72' }}><strong>Notes/Remarks:</strong> {item.notes || item.remarks || item.details || 'None'}</div>
                    </Col>
                </Row>

                {/* Render Travel Details Section if available */}
                {renderTravelDetails(travelDetails)}

                {isAdmin && (
                    <div style={{ background: '#f1f5f9', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                        <h6 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '10px' }}>Change Approval Status</h6>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <Button
                                size="sm"
                                variant="success"
                                style={{ fontWeight: 700, padding: '6px 16px' }}
                                onClick={() => { onUpdateStatus(item, 'APPROVED'); onClose(); }}
                            >
                                <i className="fa fa-check mr-1"></i> Mark Approved
                            </Button>
                            <Button
                                size="sm"
                                variant="danger"
                                style={{ fontWeight: 700, padding: '6px 16px' }}
                                onClick={() => { onUpdateStatus(item, 'REJECTED'); onClose(); }}
                            >
                                <i className="fa fa-times mr-1"></i> Mark Rejected
                            </Button>
                            <Button
                                size="sm"
                                variant="warning"
                                style={{ fontWeight: 700, padding: '6px 16px', color: '#fff' }}
                                onClick={() => { onUpdateStatus(item, 'PENDING'); onClose(); }}
                            >
                                <i className="fa fa-undo mr-1"></i> Reset to Pending
                            </Button>
                        </div>
                    </div>
                )}

                {item.proof_urls && item.proof_urls.length > 0 && (
                    <div>
                        <h6 style={{ fontWeight: 800, color: '#2d3436', marginBottom: '15px' }}>Receipts & Proofs</h6>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            {item.proof_urls.map((url, idx) => (
                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                    <img src={url} alt={`proof-${idx}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }} />
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

const DeleteConfirmationModal = ({ show, onClose, onConfirm, data, isDeleting }) => {
    if (!data) return null;
    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Body style={{ padding: '30px', textAlign: 'center' }}>
                <div style={{ 
                    width: '70px', 
                    height: '70px', 
                    background: '#fff5f5', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                }}>
                    <i className="fa fa-trash-o" style={{ color: '#ff7675', fontSize: '32px' }}></i>
                </div>
                <h4 style={{ color: '#2d3436', marginBottom: '8px', fontWeight: 800, fontSize: '20px' }}>Delete Expense?</h4>
                <p style={{ color: '#636e72', fontSize: '15px', lineHeight: '1.6', marginBottom: '28px' }}>
                    Are you sure you want to delete this expense of <strong style={{ color: '#eb4d4b', fontSize: '17px' }}>₹{data.amount}</strong> for <strong>{data.category}</strong>?<br/>
                    <span style={{ fontSize: '12px', color: '#b2bec3', marginTop: '4px', display: 'block' }}>This action is permanent and cannot be reversed.</span>
                </p>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                    <Button 
                        variant="light" 
                        onClick={onClose} 
                        disabled={isDeleting}
                        style={{ flex: 1, fontWeight: 700, borderRadius: '12px', padding: '12px', border: '1px solid #eee', background: 'white' }}
                    >
                        Keep it
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={onConfirm} 
                        disabled={isDeleting}
                        style={{ flex: 1, fontWeight: 700, borderRadius: '12px', padding: '12px', background: '#eb4d4b', border: 'none' }}
                    >
                        {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

const NectarExpenses = ({ apiaryId, isGlobal = true, onNavigateToAdvances, isAdmin = undefined }) => {
    const userIsAdmin = isAdmin !== undefined ? isAdmin : checkIsAdmin();
    const dispatch = useDispatch();
    const { allUsers = [] } = useSelector(state => state.allUsers || {});
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [userNames, setUserNames] = useState({});
    const [supervisors, setSupervisors] = useState([]);

    useEffect(() => {
        getSupervisors().then(setSupervisors).catch(() => []);
        if (!allUsers || allUsers.length === 0) {
            dispatch(getAllUsers());
        }
    }, [dispatch, allUsers]);

    const getReportingToName = (row) => resolveSupervisorName(row, supervisors, userNames);

    // Bulk & Row Selection States
    const [selectedRowIds, setSelectedRowIds] = useState([]);

    // Approval Comment Modal State
    const [approvalModal, setApprovalModal] = useState({ open: false, expense: null, targetStatus: 'APPROVED' });
    const [approvalComment, setApprovalComment] = useState('');
    const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);

    // Allocation States
    const [advances, setAdvances] = useState([]);
    const [expenseToAllocate, setExpenseToAllocate] = useState(null);
    const [isAllocating, setIsAllocating] = useState(false);

    // Filter States
    const [filters, setFilters] = useState({
        category: 'all',
        paid_by: 'all',
        employee_id: 'all',
        type: 'all',
        approval_status: 'all',
        date_from: '',
        date_to: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [allExpenses, setAllExpenses] = useState([]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const res = apiaryId 
                    ? await getExpensesWithAllocation(apiaryId, {}) 
                    : { expenses: await getAllExpensesGlobal() };
                const list = res.expenses || res || [];
                setAllExpenses(list);
            } catch (err) {
                console.error('Failed to fetch all expenses for people list:', err);
            }
        };
        fetchAll();
    }, [apiaryId]);

    const uniquePeople = useMemo(() => {
        const peopleMap = {};
        allExpenses.forEach(e => {
            const name = e.user_name || e.paid_by_name || e.created_by_name || e.incurred_by_role;
            const rawId = e.paid_by || e.created_by;
            let id = null;
            if (rawId) {
                if (typeof rawId === 'string') id = rawId;
                else if (typeof rawId === 'object') id = rawId._id || rawId.$oid || rawId.id || (rawId.toString && rawId.toString() !== '[object Object]' ? rawId.toString() : null);
            }
            if (name && typeof name === 'string' && name.trim()) {
                peopleMap[name] = id || name;
            }
        });
        return peopleMap;
    }, [allExpenses]);

    const uniqueEmpIds = useMemo(() => {
        const empSet = new Set();
        allExpenses.forEach(e => {
            const empId = resolveEmployeeId(e, supervisors, allUsers);
            if (empId && typeof empId === 'string' && empId.trim()) {
                empSet.add(empId.trim());
            }
        });
        return Array.from(empSet).sort();
    }, [allExpenses, supervisors, allUsers]);

    const fetchExpensesData = async () => {
        setLoading(true);
        try {
            const apiFilters = {};
            if (filters.category !== 'all') apiFilters.category = filters.category;
            if (filters.paid_by !== 'all') apiFilters.paid_by = filters.paid_by;
            if (filters.date_from) apiFilters.date_from = filters.date_from;
            if (filters.date_to) apiFilters.date_to = filters.date_to;

            let fetchedList = [];
            if (apiaryId) {
                const res = await getExpensesWithAllocation(apiaryId, apiFilters);
                fetchedList = res.expenses || [];
            } else {
                const res = await getAllExpensesGlobal();
                fetchedList = Array.isArray(res) ? res : (res.expenses || []);
                
                if (filters.type === 'apiary') fetchedList = fetchedList.filter(e => checkIsApiary(e));
                else if (filters.type === 'non_apiary') fetchedList = fetchedList.filter(e => !checkIsApiary(e));

                if (filters.category !== 'all') fetchedList = fetchedList.filter(e => e.category?.toLowerCase() === filters.category.toLowerCase());
                if (filters.paid_by !== 'all') {
                    fetchedList = fetchedList.filter(e => {
                        const rawId = e.paid_by || e.created_by;
                        let idStr = typeof rawId === 'string' ? rawId : (rawId?._id || rawId?.$oid || rawId?.id || '').toString();
                        return idStr === filters.paid_by || e.incurred_by_role === filters.paid_by;
                    });
                }
                if (filters.employee_id && filters.employee_id !== 'all') {
                    fetchedList = fetchedList.filter(e => {
                        const empId = resolveEmployeeId(e, supervisors, allUsers);
                        return empId && String(empId).trim().toLowerCase() === filters.employee_id.trim().toLowerCase();
                    });
                }
                if (filters.date_from) fetchedList = fetchedList.filter(e => new Date(e.expense_date) >= new Date(filters.date_from));
                if (filters.date_to) fetchedList = fetchedList.filter(e => new Date(e.expense_date) <= new Date(filters.date_to));
            }

            if (filters.approval_status !== 'all') {
                fetchedList = fetchedList.filter(e => e.approval_status === filters.approval_status);
            }

            setExpenses(fetchedList);

            // Fetch user details for any IDs not already in state
            const userIdsToFetch = new Set();
            fetchedList.forEach(e => {
                if (e.paid_by && !e.paid_by_name && typeof e.paid_by === 'string') userIdsToFetch.add(e.paid_by);
                if (e.created_by && !e.created_by_name && typeof e.created_by === 'string') userIdsToFetch.add(e.created_by);
                if (e.reporting_to && typeof e.reporting_to === 'string') userIdsToFetch.add(e.reporting_to);
            });

            if (userIdsToFetch.size > 0) {
                const namesMap = { ...userNames };
                await Promise.all(
                    Array.from(userIdsToFetch).map(async (uid) => {
                        if (!namesMap[uid]) {
                            try {
                                const details = await getUserDetails(uid);
                                if (details && (details.name || details.username)) {
                                    namesMap[uid] = details.name || details.username;
                                }
                            } catch (e) {
                                // silent fallback
                            }
                        }
                    })
                );
                setUserNames(namesMap);
            }
        } catch (error) {
            console.error("Failed to load expenses:", error);
            setExpenses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpensesData();
    }, [apiaryId, filters.category, filters.paid_by, filters.employee_id, filters.type, filters.approval_status, filters.date_from, filters.date_to]);

    const filteredExpenses = useMemo(() => {
        let sortedAndFiltered = [...expenses].sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date));

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            sortedAndFiltered = sortedAndFiltered.filter(e => {
                const empId = (resolveEmployeeId(e, supervisors, allUsers) || '').toLowerCase();
                const name = (e.user_name || e.paid_by_name || e.created_by_name || e.incurred_by_role || '').toLowerCase();
                const notes = (e.notes || e.remarks || e.details || '').toLowerCase();
                const cat = (e.category || '').toLowerCase();
                return empId.includes(lowerSearch) || name.includes(lowerSearch) || notes.includes(lowerSearch) || cat.includes(lowerSearch);
            });
        }
        return sortedAndFiltered;
    }, [expenses, searchTerm, supervisors, allUsers]);

    const calculateTotal = () => {
        return filteredExpenses.reduce((sum, item) => sum + (item.amount || 0), 0);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ category: 'all', paid_by: 'all', employee_id: 'all', type: 'all', approval_status: 'all', date_from: '', date_to: '' });
        setSearchTerm('');
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        const id = itemToDelete._id || itemToDelete.expense_id;
        setIsDeleting(true);
        try {
            await deleteExpense(id);
            toast.success("Expense deleted successfully!");
            setItemToDelete(null);
            fetchExpensesData();
        } catch (error) {
            console.error("Error deleting expense:", error);
            toast.error("Failed to delete expense. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Approval Handlers
    const getLoggedInApproverName = () => {
        try {
            const raw = readCookie('userData');
            if (raw) {
                const parsed = JSON.parse(raw);
                return parsed.name || parsed.user_name || parsed.fullName || parsed.email || 'Admin';
            }
        } catch (e) {
            console.error('Error parsing userData cookie:', e);
        }
        return 'Admin';
    };

    const handleSingleApproval = async (item, status, comment = '') => {
        const expenseId = item._id || item.expense_id;
        const approver = status === 'PENDING' ? null : getLoggedInApproverName();
        const isNonApiary = !checkIsApiary(item);
        try {
            await updateExpenseApprovalStatus(expenseId, status, comment, approver, isNonApiary);
            toast.success(`Expense status updated to ${status}!`);
            fetchExpensesData();
        } catch (err) {
            toast.error("Failed to update status");
        }
    };

    const handleBulkApproval = async (status) => {
        if (selectedRowIds.length === 0) {
            toast.warn("Please select at least one expense row");
            return;
        }
        setIsSubmittingApproval(true);
        const approver = status === 'PENDING' ? null : getLoggedInApproverName();
        try {
            await Promise.all(selectedRowIds.map(id => {
                const exp = expenses.find(e => (e._id || e.expense_id) === id);
                const isNonApiary = !checkIsApiary(exp);
                return updateExpenseApprovalStatus(id, status, '', approver, isNonApiary);
            }));
            toast.success(`${selectedRowIds.length} expense(s) updated to ${status}!`);
            setSelectedRowIds([]);
            fetchExpensesData();
        } catch (err) {
            toast.error("Failed bulk approval update");
        } finally {
            setIsSubmittingApproval(false);
        }
    };

    const submitCommentApproval = async () => {
        if (!approvalModal.expense) return;
        setIsSubmittingApproval(true);
        try {
            await handleSingleApproval(approvalModal.expense, approvalModal.targetStatus, approvalComment);
            setApprovalModal({ open: false, expense: null, targetStatus: 'APPROVED' });
            setApprovalComment('');
        } finally {
            setIsSubmittingApproval(false);
        }
    };

    const EXPORT_COLUMNS = [
        { label: 'Date', key: 'expense_date', formatter: (row) => row.expense_date ? new Date(row.expense_date).toLocaleDateString() : '' },
        { label: 'Amount', key: 'amount', path: 'amount' },
        { label: 'Expensed By', key: 'incurred_by_role', formatter: (row) => row.user_name || row.paid_by_name || row.created_by_name || row.incurred_by_role || 'Unknown' },
        { label: 'Reporting To', key: 'reporting_to', formatter: (row) => getReportingToName(row) || '' },
        { label: 'Category', key: 'category', path: 'category' },
        { label: 'Type', key: 'type', formatter: (row) => checkIsApiary(row) ? 'Apiary' : 'Non-Apiary' },
        { label: 'Approval Status', key: 'approval_status', formatter: (row) => row.approval_status || 'PENDING' },
        { label: 'Approved By', key: 'approved_by_name', formatter: (row) => row.approved_by_name || (typeof row.approved_by === 'object' ? (row.approved_by?.name || row.approved_by?.username) : row.approved_by) || '' },
        { label: 'Notes / Remarks', key: 'notes', formatter: (row) => row.notes || row.remarks || '' }
    ];

    // Table Columns with Direct Interactive Status Selector Dropdown on Every Row
    const columns = [
        {
            dataField: 'expense_date',
            text: 'Date',
            sort: true,
            formatter: (cell) => new Date(cell).toLocaleDateString(),
            style: { fontWeight: 600, color: '#2d3436' }
        },
        {
            dataField: 'amount',
            text: 'Amount',
            sort: true,
            formatter: (cell, row) => (
                <span style={{ color: row.allocated_to_advance ? '#0984e3' : '#ff7675', fontWeight: 'bold' }}>
                    ₹ {cell.toLocaleString()}
                </span>
            )
        },
        {
            dataField: 'employee_id_col',
            text: 'Employee ID',
            sort: true,
            formatter: (cell, row) => {
                const empId = resolveEmployeeId(row, supervisors, allUsers);
                if (!empId) return <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>;
                return (
                    <span style={{
                        fontWeight: 700,
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        color: '#2563eb',
                        background: '#eff6ff',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        border: '1px solid #bfdbfe'
                    }}>
                        {empId}
                    </span>
                );
            }
        },
        {
            dataField: 'incurred_by_role',
            text: 'Expensed By',
            sort: true,
            formatter: (cell, row) => {
                const name = row.user_name || row.paid_by_name || row.created_by_name || cell;
                const empId = resolveEmployeeId(row, supervisors, allUsers);
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ background: '#f1f2f6', padding: '3px 7px', borderRadius: '4px', fontSize: '13px', fontWeight: 500, width: 'fit-content' }}>
                            {name || 'Unknown'}
                        </span>
                        {empId && (
                            <span style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '1px 6px', borderRadius: '3px', border: '1px solid #bfdbfe', width: 'fit-content' }}>
                                {empId}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            dataField: 'reporting_to',
            text: 'Reporting To',
            sort: true,
            formatter: (cell, row) => {
                const repName = getReportingToName(row);
                if (!repName) {
                    return <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>;
                }
                return (
                    <span style={{ 
                        background: '#f8fafc', 
                        color: '#334155', 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '13px',
                        fontWeight: 600,
                        border: '1px solid #e2e8f0',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <i className="fa fa-user-circle-o" style={{ color: '#64748b' }}></i>
                        {repName}
                    </span>
                );
            }
        },
        {
            dataField: 'category',
            text: 'Category',
            sort: true,
            formatter: (cell, row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#0984e3', fontWeight: 600 }}>{cell}</span>
                    {checkIsApiary(row) ? (
                        <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>Apiary</span>
                    ) : (
                        <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>Non-Apiary</span>
                    )}
                </div>
            )
        },
        {
            dataField: 'proof_urls',
            text: 'Receipts',
            formatter: (cell, row) => {
                const count = cell ? cell.length : 0;
                if (count === 0) return <span style={{ color: '#b2bec3', fontSize: '13px' }}>None</span>;
                return (
                    <div style={{ cursor: 'pointer' }} onClick={() => setSelectedItem(row)}>
                        <i className="fa fa-paperclip" style={{ color: '#ff8701', marginRight: 4 }} />
                        <span style={{ color: '#ff8701', fontWeight: 600, fontSize: '13px' }}>{count} file(s)</span>
                    </div>
                );
            }
        },
        {
            dataField: 'approval_status',
            text: 'Approval Status',
            sort: true,
            formatter: (cell, row) => {
                const currentStatus = cell || 'PENDING';
                if (!userIsAdmin) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 800,
                                    fontSize: '11px',
                                    padding: '4px 10px',
                                    display: 'inline-block',
                                    textAlign: 'center',
                                    background: currentStatus === 'APPROVED' ? '#dcfce7' : currentStatus === 'REJECTED' ? '#fee2e2' : '#fef3c7',
                                    color: currentStatus === 'APPROVED' ? '#15803d' : currentStatus === 'REJECTED' ? '#b91c1c' : '#b45309',
                                    border: currentStatus === 'APPROVED' ? '1px solid #86efac' : currentStatus === 'REJECTED' ? '1px solid #fca5a5' : '1px solid #fde68a',
                                    width: '110px'
                                }}
                            >
                                {currentStatus === 'APPROVED' ? '🟢 APPROVED' : currentStatus === 'REJECTED' ? '🔴 REJECTED' : '⏳ PENDING'}
                            </span>
                            {currentStatus !== 'PENDING' && (row.approved_by_name || row.approved_by) && (
                                <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>
                                    By {row.approved_by_name || (typeof row.approved_by === 'object' ? (row.approved_by?.name || row.approved_by?.username) : null) || resolveSupervisorName({ reporting_to: row.approved_by }, supervisors, userNames) || row.approved_by}
                                </span>
                            )}
                        </div>
                    );
                }
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={currentStatus}
                            onChange={(e) => {
                                e.stopPropagation();
                                const newStatus = e.target.value;
                                if (newStatus === 'REJECTED') {
                                    setApprovalModal({ open: true, expense: row, targetStatus: 'REJECTED' });
                                } else {
                                    handleSingleApproval(row, newStatus);
                                }
                            }}
                            style={{
                                borderRadius: '8px',
                                fontWeight: 800,
                                fontSize: '12px',
                                padding: '3px 8px',
                                height: '32px',
                                background: currentStatus === 'APPROVED' ? '#dcfce7' : currentStatus === 'REJECTED' ? '#fee2e2' : '#fef3c7',
                                color: currentStatus === 'APPROVED' ? '#15803d' : currentStatus === 'REJECTED' ? '#b91c1c' : '#b45309',
                                border: currentStatus === 'APPROVED' ? '1px solid #86efac' : currentStatus === 'REJECTED' ? '1px solid #fca5a5' : '1px solid #fde68a',
                                cursor: 'pointer',
                                width: '130px'
                            }}
                        >
                            <option value="PENDING">⏳ PENDING</option>
                            <option value="APPROVED">🟢 APPROVED</option>
                            <option value="REJECTED">🔴 REJECTED</option>
                        </Form.Control>
                        {currentStatus !== 'PENDING' && (row.approved_by_name || row.approved_by) && (
                            <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>
                                By {row.approved_by_name || (typeof row.approved_by === 'object' ? (row.approved_by?.name || row.approved_by?.username) : null) || resolveSupervisorName({ reporting_to: row.approved_by }, supervisors, userNames) || row.approved_by}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            dataField: 'actions',
            text: 'Actions',
            formatter: (_, row) => (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <ActionButton className="view" title="View Details" onClick={(e) => { e.stopPropagation(); setSelectedItem(row); }}>
                        <i className="fa fa-eye"></i>
                    </ActionButton>
                    {userIsAdmin && (
                        <ActionButton className="delete" title="Delete Expense" onClick={(e) => { e.stopPropagation(); setItemToDelete(row); }}>
                            <i className="fa fa-trash-o"></i>
                        </ActionButton>
                    )}
                </div>
            )
        }
    ];

    const selectRow = {
        mode: 'checkbox',
        clickToSelect: false,
        onSelect: (row, isSelect) => {
            const id = row._id || row.expense_id;
            if (isSelect) setSelectedRowIds(prev => [...prev, id]);
            else setSelectedRowIds(prev => prev.filter(x => x !== id));
        },
        onSelectAll: (isSelect, rows) => {
            const ids = rows.map(r => r._id || r.expense_id);
            if (isSelect) setSelectedRowIds(ids);
            else setSelectedRowIds([]);
        }
    };

    return (
        <MainContainer isGlobal={isGlobal}>
            <HeaderSection>
                <TotalBadge>
                    <div style={{ width: '40px', height: '40px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '15px' }}>
                        <i className="fa fa-calculator" style={{ fontSize: '18px', color: '#ff7e5f' }}></i>
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Total Expense</div>
                        <div style={{ fontSize: '22px', fontWeight: 900 }}>₹ {calculateTotal().toLocaleString()}</div>
                    </div>
                </TotalBadge>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowExportModal(true)}
                        style={{ padding: '10px 20px', fontWeight: 600 }}
                    >
                        <i className="fa fa-download mr-2"></i> Export CSV
                    </Button>
                    {userIsAdmin && (
                        <button className="add-expense-btn" onClick={() => setShowAddModal(true)}>
                            <i className="fa fa-plus"></i> Add Expense
                        </button>
                    )}
                </div>
            </HeaderSection>

            {/* Filter Card */}
            <FilterCard>
                <Row className="align-items-end">
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <Form.Group className="mb-0">
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#636e72' }}>Approval Status</Form.Label>
                            <Form.Control
                                as="select"
                                name="approval_status"
                                value={filters.approval_status}
                                onChange={handleFilterChange}
                                style={{ borderRadius: '8px', border: '1px solid #dfe6e9', fontWeight: 600 }}
                            >
                                <option value="all">All Status</option>
                                <option value="PENDING">Pending</option>
                                <option value="APPROVED">Approved</option>
                                <option value="REJECTED">Rejected</option>
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <Form.Group className="mb-0">
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#636e72' }}>Expensed By</Form.Label>
                            <Form.Control
                                as="select"
                                name="paid_by"
                                value={filters.paid_by}
                                onChange={handleFilterChange}
                                style={{ borderRadius: '8px', border: '1px solid #dfe6e9' }}
                            >
                                <option value="all">All People</option>
                                {Object.entries(uniquePeople).map(([name, id]) => (
                                    <option key={name} value={id}>{name}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    {!apiaryId && (
                        <Col lg={2} md={4} sm={6} className="mb-3">
                            <Form.Group className="mb-0">
                                <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#636e72' }}>Expense Type</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="type"
                                    value={filters.type}
                                    onChange={handleFilterChange}
                                    style={{ borderRadius: '8px', border: '1px solid #dfe6e9', fontWeight: 600, color: filters.type === 'non_apiary' ? '#475569' : filters.type === 'apiary' ? '#0369a1' : '#2d3436' }}
                                >
                                    <option value="all">All Types</option>
                                    <option value="apiary">🐝 Apiary Expenses</option>
                                    <option value="non_apiary">🏢 Non-Apiary Expenses</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    )}
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <Form.Group className="mb-0">
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#636e72' }}>Category</Form.Label>
                            <Form.Control
                                as="select"
                                name="category"
                                value={filters.category}
                                onChange={handleFilterChange}
                                style={{ borderRadius: '8px', border: '1px solid #dfe6e9' }}
                            >
                                <option value="all">All Categories</option>
                                {EXPENSE_CATEGORIES.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <Form.Group className="mb-0">
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#636e72' }}>Employee ID</Form.Label>
                            <Form.Control
                                as="select"
                                name="employee_id"
                                value={filters.employee_id}
                                onChange={handleFilterChange}
                                style={{ borderRadius: '8px', border: '1px solid #dfe6e9', fontWeight: 600, color: filters.employee_id !== 'all' ? '#1652f0' : '#2d3436' }}
                            >
                                <option value="all">All Employee IDs</option>
                                {uniqueEmpIds.map(empId => (
                                    <option key={empId} value={empId}>{empId}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col lg={3} md={6} sm={12} className="mb-3">
                        <Form.Group className="mb-0">
                            <Form.Label style={{ fontSize: '13px', fontWeight: 600, color: '#636e72' }}>Search (ID / Name / Notes)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Search BW0001, name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ borderRadius: '8px', border: '1px solid #dfe6e9' }}
                            />
                        </Form.Group>
                    </Col>
                    <Col lg={12} className="text-right mt-1 d-flex justify-content-end gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={clearFilters} style={{ borderRadius: '8px', fontWeight: 600, padding: '6px 18px' }}>
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </FilterCard>

            {/* Table with Default 100 rows/page */}
            <div className="modern-table">
                <BootstrapTable
                    keyField="_id"
                    data={filteredExpenses}
                    columns={columns}
                    selectRow={userIsAdmin ? selectRow : undefined}
                    pagination={paginationFactory({
                        sizePerPage: 100,
                        sizePerPageList: [
                            { text: '50', value: 50 },
                            { text: '100', value: 100 },
                            { text: '200', value: 200 },
                            { text: '500', value: 500 },
                            { text: '1000', value: 1000 }
                        ],
                        showTotal: true
                    })}
                />
            </div>

            {/* TeamNest Bulk Approval Controls */}
            {userIsAdmin && selectedRowIds.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '15px', background: '#fff', padding: '15px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#475569', alignSelf: 'center', marginRight: 'auto' }}>
                        {selectedRowIds.length} row(s) selected
                    </span>
                    <Button
                        variant="outline-success"
                        style={{ borderRadius: '8px', fontWeight: 700, padding: '8px 20px' }}
                        disabled={isSubmittingApproval}
                        onClick={() => handleBulkApproval('APPROVED')}
                    >
                        <i className="fa fa-check mr-1"></i> Approve Selected
                    </Button>
                    <Button
                        variant="outline-danger"
                        style={{ borderRadius: '8px', fontWeight: 700, padding: '8px 20px' }}
                        disabled={isSubmittingApproval}
                        onClick={() => handleBulkApproval('REJECTED')}
                    >
                        <i className="fa fa-times mr-1"></i> Reject Selected
                    </Button>
                </div>
            )}

            <GenericExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={filteredExpenses}
                columns={EXPORT_COLUMNS}
                title="Export Expenses to CSV"
                filenamePrefix="expenses_export"
            />

            {/* Add Expense Modal */}
            <AddExpenseModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={fetchExpensesData}
                currentApiaryId={apiaryId}
            />

            {/* Approval Modal */}
            <Modal show={approvalModal.open} onHide={() => setApprovalModal({ open: false, expense: null, targetStatus: 'APPROVED' })} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontWeight: 800 }}>
                        {approvalModal.targetStatus === 'REJECTED' ? '🔴 Reject Expense' : '🟢 Approve Expense'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '14px', color: '#475569' }}>
                        Add an optional comment/reason for marking this expense as <strong>{approvalModal.targetStatus}</strong>:
                    </p>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter comment or rejection reason..."
                        value={approvalComment}
                        onChange={e => setApprovalComment(e.target.value)}
                        style={{ borderRadius: '8px' }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setApprovalModal({ open: false, expense: null, targetStatus: 'APPROVED' })}>
                        Cancel
                    </Button>
                    <Button
                        variant={approvalModal.targetStatus === 'REJECTED' ? 'danger' : 'success'}
                        disabled={isSubmittingApproval}
                        onClick={submitCommentApproval}
                        style={{ fontWeight: 700 }}
                    >
                        Submit {approvalModal.targetStatus}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Expense Detail & Status Change Modal */}
            <ExpenseDetailModal
                item={selectedItem}
                isAdmin={userIsAdmin}
                reportingToName={selectedItem ? getReportingToName(selectedItem) : ''}
                onClose={() => setSelectedItem(null)}
                onUpdateStatus={handleSingleApproval}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                show={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onConfirm={handleDelete}
                data={itemToDelete}
                isDeleting={isDeleting}
            />
        </MainContainer>
    );
};

export default NectarExpenses;
